import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from "flarum/forum/app";
import Modal from "flarum/common/components/Modal";
import Button from "flarum/common/components/Button";
import Avatar from "flarum/common/components/Avatar";
import Switch from "flarum/common/components/Switch";
import username from "flarum/common/helpers/username";
import classList from "flarum/common/utils/classList";
import userLink from "../utils/userLink";
import { NotificationLevel } from "../../common/models/Channel";
import chatState from "../state/chat";
import { isOnline } from "../utils/presence";
import { MembersSkeleton } from "./Skeletons";
import { channelIcon } from "../utils/channelIcon";
import { sendKeyPreference } from "../utils/shortcuts";
import AddMembersModal from "./AddMembersModal";
import iconLabel from "../utils/iconLabel";
/**
 * Channel details: notification preferences, the member list, and the moderation
 * actions that change a channel's state.
 *
 * Distinct from ChannelFormModal, which edits what a channel *is* (name, category,
 * threading) and needs the edit permission. This is what any member may see and
 * adjust for themselves, with the state-changing actions gated per capability
 * flag — so a plain member gets a useful panel rather than a locked one.
 */
export default class ChannelInfoModal extends Modal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "tab", "settings");
    _defineProperty(this, "members", []);
    /** People invited and not yet answered. Served to managers only. */
    _defineProperty(this, "invited", []);
    _defineProperty(this, "loadingMembers", false);
    _defineProperty(this, "loadedMembers", false);
    _defineProperty(this, "memberFilter", "");
    _defineProperty(this, "working", false);
    /** Stops listening for membership pushes; set while the modal is open. */
    _defineProperty(this, "stopListening", null);
    /**
     * Which immediate action is running, or null.
     *
     * `working` answers "is anything in flight", which is what every control reads
     * to disable itself. A spinner is a narrower claim — it says *this* button
     * started something — and driving them all from the one flag meant changing
     * the notification level spun Close and Archive along with it.
     */
    _defineProperty(this, "pending", null);
  }
  className() {
    return "ChatModal ChatChannelInfoModal Modal--medium";
  }
  title() {
    const channel = this.attrs.channel;
    return m('[', null, channelIcon(channel, "ChatChannelInfo-icon"), channel.displayName());
  }
  content() {
    return m("div", {
      className: "ChatChannelInfo"
    }, m("div", {
      className: "ChatChannelInfo-tabs"
    }, this.tabButton("settings", "ramon-chat.forum.info.tab_settings"), this.tabButton("members", "ramon-chat.forum.info.tab_members")), this.tab === "settings" ? this.settings() : this.memberTab());
  }
  tabButton(tab, key) {
    return m("button", {
      type: "button",
      className: classList("ChatChannelInfo-tab", {
        "ChatChannelInfo-tab--active": this.tab === tab
      }),
      onclick: () => {
        this.tab = tab;
        if (tab === "members") this.loadMembers();
      }
    }, app.translator.trans(key));
  }

  // ── Settings tab ───────────────────────────────────────────────────────────

  settings() {
    var _channel$notification;
    const channel = this.attrs.channel;
    return m("div", null, channel.description() ? m("div", {
      className: "ChatChannelInfo-section"
    }, m("div", {
      className: "ChatChannelInfo-section-label"
    }, app.translator.trans("ramon-chat.forum.info.description")), m("div", null, channel.description())) : null, m("div", {
      className: "ChatChannelInfo-section"
    }, m("div", {
      className: "ChatChannelInfo-section-label"
    }, app.translator.trans("ramon-chat.forum.info.notifications")), m("label", null, app.translator.trans("ramon-chat.forum.info.notification_level"), m("select", {
      className: "FormControl",
      value: String((_channel$notification = channel.notificationLevel()) != null ? _channel$notification : NotificationLevel.Mentions),
      onchange: e => this.saveNotifications(Number(e.target.value), null)
    }, m("option", {
      value: String(NotificationLevel.Always)
    }, app.translator.trans("ramon-chat.forum.info.level_always", {}, true)), m("option", {
      value: String(NotificationLevel.Mentions)
    }, app.translator.trans("ramon-chat.forum.info.level_mentions", {}, true)), m("option", {
      value: String(NotificationLevel.Never)
    }, app.translator.trans("ramon-chat.forum.info.level_never", {}, true)))), m("div", {
      className: "helpText"
    }, app.translator.trans(this.notificationLevelHelp(channel))), m(Switch, {
      state: Boolean(channel.isMuted()),
      onchange: value => this.saveNotifications(null, value),
      disabled: this.working
    }, app.translator.trans("ramon-chat.forum.info.mute")), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.info.mute_help"))), this.composerSection(), this.moderation());
  }

  /**
   * How this member's own composer behaves.
   *
   * Its own section rather than a row under NOTIFICATIONS, and worth being
   * explicit about why it is in a per-channel panel at all: which key sends is a
   * setting for the person, not for the channel, and it applies everywhere. The
   * section sits here because this is the panel a member already opens to adjust
   * the chat for themselves — but the help line says "every channel" so nobody
   * reads the surrounding modal as the scope.
   *
   * Guests never reach this: the chat requires an account, and `savePreferences`
   * needs a user to save against.
   */
  composerSection() {
    if (!app.session.user) return null;
    const preference = sendKeyPreference();
    return m("div", {
      className: "ChatChannelInfo-section"
    }, m("div", {
      className: "ChatChannelInfo-section-label"
    }, app.translator.trans("ramon-chat.forum.info.composer")), m("label", null, app.translator.trans("ramon-chat.forum.info.send_key"), m("select", {
      className: "FormControl",
      value: preference,
      disabled: this.working,
      onchange: e => this.saveSendKey(e.target.value)
    }, m("option", {
      value: "enter"
    }, app.translator.trans("ramon-chat.forum.info.send_key_enter", {}, true)), m("option", {
      value: "ctrl"
    }, app.translator.trans("ramon-chat.forum.info.send_key_ctrl", {}, true)))), m("div", {
      className: "helpText"
    }, app.translator.trans(preference === "ctrl" ? "ramon-chat.forum.info.send_key_help_ctrl" : "ramon-chat.forum.info.send_key_help_enter")));
  }

  /** The line describing the notification level currently selected. */
  notificationLevelHelp(channel) {
    var _channel$notification2;
    switch ((_channel$notification2 = channel.notificationLevel()) != null ? _channel$notification2 : NotificationLevel.Mentions) {
      case NotificationLevel.Always:
        return "ramon-chat.forum.info.level_always_help";
      case NotificationLevel.Never:
        return "ramon-chat.forum.info.level_never_help";
      default:
        return "ramon-chat.forum.info.level_mentions_help";
    }
  }

  /**
   * Actions that change the channel for everyone. Each is drawn only when the
   * server said the actor may do it, so nothing here is present-and-rejected.
   */
  moderation() {
    const channel = this.attrs.channel;
    const items = [];
    if (channel.canClose()) {
      const closed = channel.status() === "closed";
      items.push(m(Button, {
        className: "Button",
        icon: closed ? "fas fa-lock-open" : "fas fa-lock",
        loading: this.pending === "status",
        disabled: this.working,
        onclick: () => this.setStatus(closed ? "open" : "closed")
      }, app.translator.trans(closed ? "ramon-chat.forum.info.reopen_channel" : "ramon-chat.forum.info.close_channel")));
    }
    if (channel.canArchive() && !channel.archivedAt()) {
      items.push(m(Button, {
        className: "Button",
        icon: "fas fa-box-archive",
        loading: this.pending === "archive",
        disabled: this.working,
        onclick: () => this.archive()
      }, app.translator.trans("ramon-chat.forum.info.archive_channel")));
    }
    if (items.length === 0 && !channel.canDelete()) return null;
    return m('[', null, items.length > 0 ? m("div", {
      className: "ChatChannelInfo-section"
    }, items) : null, channel.canDelete() ? m("div", {
      className: "ChatChannelInfo-danger"
    }, m(Button, {
      className: "Button ChatChannelInfo-deleteButton",
      icon: "fas fa-trash",
      loading: this.pending === "delete",
      disabled: this.working,
      onclick: () => this.destroy()
    }, app.translator.trans("ramon-chat.forum.info.delete_channel"))) : null);
  }

  // ── Members tab ────────────────────────────────────────────────────────────

  memberTab() {
    if (this.loadingMembers) {
      return m("div", {
        className: "ChatChannelInfo-section"
      }, MembersSkeleton());
    }
    const term = this.memberFilter.trim().toLowerCase();
    const shown = term ? this.members.filter(user => (user.displayName() + " " + user.username()).toLowerCase().includes(term)) : this.members;
    return m("div", {
      className: "ChatChannelInfo-section"
    }, m("div", {
      className: "ChatChannelInfo-memberHeader"
    }, m("span", {
      className: "ChatChannelInfo-memberCount"
    }, app.translator.trans("ramon-chat.forum.channel.members", {
      count: this.members.length
    })), this.attrs.channel.canManageMembers() ? m(Button, {
      className: "Button Button--primary Button--compact",
      icon: "fas fa-user-plus",
      disabled: this.working,
      onclick: () => this.openAddMembers()
    }, app.translator.trans("ramon-chat.forum.add_members.title")) : null), this.attrs.channel.canManageModerators() ? m("p", {
      className: "helpText ChatChannelInfo-moderatorHelp"
    }, app.translator.trans("ramon-chat.forum.info.moderator_help")) : null, m("input", {
      className: "FormControl ChatChannelInfo-filter",
      type: "search",
      placeholder: app.translator.trans("ramon-chat.forum.info.member_search", {}, true),
      value: this.memberFilter,
      oninput: e => {
        this.memberFilter = e.target.value;
      }
    }), m("div", {
      className: "ChatChannelInfo-memberList"
    }, shown.map(user => m("div", {
      key: user.id(),
      className: classList("ChatChannelInfo-member", {
        "ChatChannelInfo-member--online": isOnline(user)
      })
    }, m(Avatar, {
      user: user,
      className: "Avatar"
    }), m("span", null, userLink(user)), this.memberBadge(user), this.memberControls(user))), shown.length === 0 ? m("div", {
      className: "ChatBrowse-empty"
    }, app.translator.trans("ramon-chat.forum.info.no_members")) : null), this.invitedList());
  }

  /**
   * Who has been asked in and not answered, for whoever manages the list.
   *
   * Under the members rather than among them: an invitation is not a
   * membership, and a name in the member list that cannot be messaged would
   * be a lie. Each row can be withdrawn, which is the manager's half of the
   * exchange the invitee's accept and decline are the other half of.
   */
  invitedList() {
    if (!this.attrs.channel.canManageMembers() || this.invited.length === 0) {
      return null;
    }
    return m("div", {
      className: "ChatChannelInfo-invited"
    }, m("div", {
      className: "ChatChannelInfo-memberHeader"
    }, m("span", {
      className: "ChatChannelInfo-memberCount"
    }, app.translator.trans("ramon-chat.forum.info.invited_heading", {
      count: this.invited.length
    }))), m("div", {
      className: "ChatChannelInfo-memberList"
    }, this.invited.map(user => m("div", {
      key: user.id(),
      className: "ChatChannelInfo-member ChatChannelInfo-member--invited"
    }, m(Avatar, {
      user: user,
      className: "Avatar"
    }), m("span", null, userLink(user)), m("span", {
      className: "ChatChannelInfo-member-badge"
    }, app.translator.trans("ramon-chat.forum.info.invited_badge")), m(Button, Object.assign({
      className: "Button Button--icon Button--flat ChatChannelInfo-member-remove",
      icon: "fas fa-user-xmark",
      disabled: this.working
    }, iconLabel(app.translator.trans("ramon-chat.forum.info.cancel_invite", {
      username: username(user)
    }, true)), {
      onclick: () => this.cancelInvite(user)
    }))))));
  }

  /**
   * Withdraws an invitation. The invitee's notification goes with it, so
   * confirmed first: the person may be reading it at that very moment.
   */
  async cancelInvite(user) {
    const confirmed = confirm(app.translator.trans("ramon-chat.forum.info.cancel_invite_confirm", {
      username: username(user)
    }, true));
    if (!confirmed) return;
    this.working = true;
    m.redraw();
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-channels/").concat(this.attrs.channel.id(), "/invites/cancel"),
        body: {
          data: {
            attributes: {
              userId: Number(user.id())
            }
          }
        }
      });
      if (payload != null && payload.data) app.store.pushPayload(payload);
      this.invited = this.invited.filter(entry => entry.id() !== user.id());
      app.alerts.show({
        type: "success"
      }, app.translator.trans("ramon-chat.forum.info.invite_cancelled", {
        username: username(user)
      }));
    } catch (e) {
      var _e$response$errors$0$, _e$response;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$ = e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) != null ? _e$response$errors$0$ : app.translator.trans("ramon-chat.forum.info.save_failed"));
    } finally {
      this.working = false;
      m.redraw();
    }
  }
  isOwner(user) {
    const creatorId = this.attrs.channel.creatorId();
    return creatorId != null && Number(creatorId) === Number(user.id());
  }
  isModerator(user) {
    var _this$attrs$channel$m;
    return ((_this$attrs$channel$m = this.attrs.channel.moderatorIds()) != null ? _this$attrs$channel$m : []).some(id => Number(id) === Number(user.id()));
  }

  /** The owner's and channel moderators' labels, so the roles are visible to everyone. */
  memberBadge(user) {
    if (this.isOwner(user)) {
      return m("span", {
        className: "ChatChannelInfo-member-badge ChatChannelInfo-member-badge--owner"
      }, app.translator.trans("ramon-chat.forum.info.owner_badge"));
    }
    if (this.isModerator(user)) {
      return m("span", {
        className: "ChatChannelInfo-member-badge"
      }, app.translator.trans("ramon-chat.forum.info.moderator_badge"));
    }
    return null;
  }

  /**
   * Promote, demote and remove — each drawn only for people the actor may
   * actually act on, so no button is a promise the server refuses to keep.
   * Nothing for yourself: leaving is what "Leave channel" is for, and the owner
   * is not something you can stop being from here.
   */
  memberControls(user) {
    var _app$session$user;
    const channel = this.attrs.channel;
    if (user.id() === ((_app$session$user = app.session.user) == null ? void 0 : _app$session$user.id())) return null;
    const controls = [];
    if (channel.canManageModerators() && !this.isOwner(user)) {
      const moderator = this.isModerator(user);
      controls.push(m(Button, Object.assign({
        className: "Button Button--icon Button--flat ChatChannelInfo-member-role",
        icon: moderator ? "fas fa-user-slash" : "fas fa-user-shield",
        disabled: this.working
      }, iconLabel(app.translator.trans(moderator ? "ramon-chat.forum.info.demote_moderator" : "ramon-chat.forum.info.promote_moderator", {
        username: username(user)
      }, true)), {
        onclick: () => this.setModerator(user, !moderator)
      })));
    }
    if (channel.canManageMembers()) {
      controls.push(m(Button, Object.assign({
        className: "Button Button--icon Button--flat ChatChannelInfo-member-remove",
        icon: "fas fa-user-minus",
        disabled: this.working
      }, iconLabel(app.translator.trans("ramon-chat.forum.info.remove_member", {
        username: username(user)
      }, true)), {
        onclick: () => this.remove(user)
      })));
    }
    return controls;
  }

  /**
   * Hands the channel's moderator role to a member, or takes it back.
   *
   * The response carries the channel with its participants, so pushing it
   * refreshes `moderatorIds` on the very model this modal is drawing from.
   */
  async setModerator(user, moderator) {
    this.working = true;
    m.redraw();
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-channels/").concat(this.attrs.channel.id(), "/moderators").concat(moderator ? "" : "/remove"),
        body: {
          data: {
            attributes: {
              userId: Number(user.id())
            }
          }
        }
      });
      if (payload != null && payload.data) app.store.pushPayload(payload);
      app.alerts.show({
        type: "success"
      }, app.translator.trans(moderator ? "ramon-chat.forum.info.moderator_promoted" : "ramon-chat.forum.info.moderator_demoted", {
        username: username(user)
      }));
    } catch (e) {
      var _e$response$errors$0$2, _e$response2;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$2 = e == null || (_e$response2 = e.response) == null || (_e$response2 = _e$response2.errors) == null || (_e$response2 = _e$response2[0]) == null ? void 0 : _e$response2.detail) != null ? _e$response$errors$0$2 : app.translator.trans("ramon-chat.forum.info.save_failed"));
    } finally {
      this.working = false;
      m.redraw();
    }
  }

  /**
   * Opens the people picker.
   *
   * Stacked on top of this modal rather than replacing it: the member list is
   * the context for the choice, and coming back to a closed dialog after adding
   * three people would mean reopening it to check they landed.
   *
   * This is how anyone gets into a private channel: it is not discoverable and
   * cannot be joined, so an existing member with `manageMembers` has to put you
   * there. Offered only when the server says the actor may — a moderator, or the
   * creator of a group conversation.
   */
  openAddMembers() {
    app.modal.show(AddMembersModal, {
      channel: this.attrs.channel,
      // Both lists: the picker shows them marked rather than offering them,
      // so someone already in, or already asked, is not a mystery absence.
      members: this.members,
      invited: this.invited,
      onAdded: users => this.onMembersInvited(users)
    }, true);
  }

  /**
   * Folds the picker's result into the pending list.
   *
   * Invited, not added: nobody is a member until they say yes, so the count
   * does not move and the names go under "invitations pending" rather than
   * into the member list.
   */
  onMembersInvited(users) {
    const known = new Set([...this.members.map(member => member.id()), ...this.invited.map(user => user.id())]);
    const fresh = users.filter(user => !known.has(user.id()));
    if (fresh.length === 0) return;
    this.invited = [...this.invited, ...fresh];
    m.redraw();
  }

  /**
   * Removes someone else from the channel.
   *
   * Confirmed first: unlike adding, this one is not obviously undoable from the
   * other side — a private channel cannot be rejoined, so the person would have to
   * be added back by hand.
   */
  async remove(user) {
    const confirmed = confirm(app.translator.trans("ramon-chat.forum.info.remove_member_confirm", {
      username: username(user)
    }, true));
    if (!confirmed) return;
    this.working = true;
    m.redraw();
    try {
      var _this$attrs$channel$u;
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-channels/").concat(this.attrs.channel.id(), "/members/remove"),
        body: {
          data: {
            attributes: {
              userId: Number(user.id())
            }
          }
        }
      });
      if (payload != null && payload.data) app.store.pushPayload(payload);
      this.members = this.members.filter(member => member.id() !== user.id());

      // Kept in step with the server's own decrement so the count does not sit
      // one high until the next fetch.
      this.attrs.channel.pushAttributes({
        userCount: Math.max(0, ((_this$attrs$channel$u = this.attrs.channel.userCount()) != null ? _this$attrs$channel$u : 1) - 1)
      });
      app.alerts.show({
        type: "success"
      }, app.translator.trans("ramon-chat.forum.info.member_removed", {
        username: username(user)
      }));
    } catch (e) {
      var _e$response$errors$0$3, _e$response3;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$3 = e == null || (_e$response3 = e.response) == null || (_e$response3 = _e$response3.errors) == null || (_e$response3 = _e$response3[0]) == null ? void 0 : _e$response3.detail) != null ? _e$response$errors$0$3 : app.translator.trans("ramon-chat.forum.info.save_failed"));
    } finally {
      this.working = false;
      m.redraw();
    }
  }
  async loadMembers() {
    var _this$stopListening;
    if (this.loadedMembers || this.loadingMembers) return;
    this.loadingMembers = true;

    // From here on, a membership push for this channel re-reads the list, so
    // an invitation answered while the tab is open is seen being answered.
    (_this$stopListening = this.stopListening) != null ? _this$stopListening : this.stopListening = chatState.onMembershipChange(channelId => {
      if (channelId !== Number(this.attrs.channel.id())) return;
      this.loadedMembers = false;
      void this.loadMembers();
    });
    try {
      // Re-fetched with the relationships included rather than read off the
      // sidebar's copy, which is loaded without participants. The pending
      // invitations ride along; the server omits them for anyone who may not
      // manage the list.
      const channel = await app.store.find("chat-channels", String(this.attrs.channel.id()), {
        include: "participants,invitedUsers"
      });
      this.members = (channel.participants() || []).filter(Boolean);
      this.invited = (channel.invitedUsers() || []).filter(Boolean);
    } catch (_unused) {
      this.members = [];
      this.invited = [];
    } finally {
      this.loadingMembers = false;
      this.loadedMembers = true;
      m.redraw();
    }
  }
  onremove(vnode) {
    var _this$stopListening2;
    super.onremove(vnode);
    (_this$stopListening2 = this.stopListening) == null || _this$stopListening2.call(this);
    this.stopListening = null;
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  async saveNotifications(level, muted) {
    const channel = this.attrs.channel;

    // Optimistic: the endpoint answers 204, so there is nothing to push back and
    // waiting would leave the control visibly lagging the click.
    channel.pushAttributes(_objectSpread(_objectSpread({}, level !== null ? {
      notificationLevel: level
    } : {}), muted !== null ? {
      isMuted: muted
    } : {}));
    this.working = true;
    m.redraw();
    try {
      await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-channels/").concat(channel.id(), "/notifications"),
        body: {
          data: {
            attributes: _objectSpread(_objectSpread({}, level !== null ? {
              notificationLevel: level
            } : {}), muted !== null ? {
              muted
            } : {})
          }
        }
      });
    } catch (_unused2) {
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.info.save_failed"));
    } finally {
      this.working = false;
      m.redraw();
    }
  }

  /**
   * Stores the send-key choice on the user.
   *
   * Optimistic like `saveNotifications`, and for a sharper reason: the composer
   * reads the preference off the same record on every keystroke, so the change
   * has to be in place before the next one — waiting for the round trip would
   * mean the first Enter after choosing still did the old thing.
   *
   * The rollback is not the redundant belt-and-braces it looks like. `Model.save`
   * does snapshot and restore on failure, but `savePreferences` mutates the
   * preferences object *in place* before calling it, so the snapshot is taken
   * with the new value already in it and core's revert restores that. Undoing it
   * here is the only thing that stops a rejected save from leaving the select —
   * and the composer — agreeing with a server that never accepted it.
   */
  async saveSendKey(value) {
    const user = app.session.user;
    if (!user) return;
    const previous = sendKeyPreference();
    if (value === previous) return;
    this.working = true;
    try {
      await user.savePreferences({
        "ramon-chat.sendWithCtrlEnter": value
      });
    } catch (_unused3) {
      await user.savePreferences({
        "ramon-chat.sendWithCtrlEnter": previous
      }).catch(() => {});
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.info.save_failed"));
    } finally {
      this.working = false;
      m.redraw();
    }
  }
  async setStatus(status) {
    await this.act("status", "/chat-channels/".concat(this.attrs.channel.id(), "/status"), {
      status
    });
  }
  async archive() {
    await this.act("archive", "/chat-channels/".concat(this.attrs.channel.id(), "/archive"), {});
  }
  async destroy() {
    if (!confirm(app.translator.trans("ramon-chat.forum.info.delete_confirm", {}, true))) return;
    this.pending = "delete";
    this.working = true;
    try {
      await this.attrs.channel.delete();
      chatState.channels = chatState.channels.filter(c => c.id() !== this.attrs.channel.id());
      if (chatState.activeChannelId === Number(this.attrs.channel.id())) {
        chatState.setActiveChannel(null);
      }
      this.hide();
    } catch (_unused4) {
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.info.save_failed"));
    } finally {
      this.pending = null;
      this.working = false;
      m.redraw();
    }
  }

  /**
   * Runs one immediate state change.
   *
   * `action` names the button that owns the spinner for the duration; `working`
   * still gates every other control, so nothing else can be started meanwhile.
   */
  async act(action, path, attributes) {
    this.pending = action;
    this.working = true;
    m.redraw();
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl")).concat(path),
        body: {
          data: {
            attributes
          }
        }
      });
      if (payload != null && payload.data) app.store.pushPayload(payload);
      this.hide();
    } catch (e) {
      var _e$response$errors$0$4, _e$response4;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$4 = e == null || (_e$response4 = e.response) == null || (_e$response4 = _e$response4.errors) == null || (_e$response4 = _e$response4[0]) == null ? void 0 : _e$response4.detail) != null ? _e$response$errors$0$4 : app.translator.trans("ramon-chat.forum.info.save_failed"));
    } finally {
      this.pending = null;
      this.working = false;
      m.redraw();
    }
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChannelInfoModal', ChannelInfoModal);